import { FormlyFieldConfig } from '@ngx-formly/core';
import { advancedSection, COMMON_FIELD_CONFIGS } from './field-config-shared';

function resolveStateTransitionSourceFieldId(field?: FormlyFieldConfig): string | undefined {
  let cursor: FormlyFieldConfig | undefined = field;

  while (cursor) {
    const model = cursor.model as { sourceFieldId?: unknown } | undefined;
    if (typeof model?.sourceFieldId === 'string' && model.sourceFieldId.trim() !== '') {
      return model.sourceFieldId;
    }
    cursor = cursor.parent;
  }

  return undefined;
}

function previewDuration(format?: string): string {
  const sampleSeconds = 93784; // 1d 2h 3m 4s
  const days = Math.floor(sampleSeconds / 86400);
  let remaining = sampleSeconds % 86400;
  const hours = Math.floor(remaining / 3600);
  remaining %= 3600;
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;

  if (typeof format === 'string' && format.trim() !== '') {
    const rendered = format
      .replaceAll('{d}', String(days))
      .replaceAll('{h}', String(hours))
      .replaceAll('{m}', String(minutes))
      .replaceAll('{s}', String(seconds))
      .replaceAll('{total_h}', String(Math.trunc(sampleSeconds / 3600)))
      .replaceAll('{total_m}', String(Math.trunc(sampleSeconds / 60)))
      .replaceAll('{total_s}', String(sampleSeconds));
    return `Preview (93784s): ${rendered}`;
  }

  return 'Preview (93784s): 1d 2h 3m 4s';
}

function previewNumber(format?: string): string {
  const sampleValue = 12345.678;
  const formatSpec = (format ?? '').trim().toLowerCase();

  if (formatSpec === '' || formatSpec === 'default') {
    return `Preview (${sampleValue}): ${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(sampleValue)}`;
  }

  const parts = formatSpec.split(':');
  const kind = parts[0];

  if (kind === 'integer') {
    return `Preview (${sampleValue}): ${new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    }).format(Math.trunc(sampleValue))}`;
  }

  if (kind === 'decimal') {
    const digits = Number(parts[1]);
    const safeDigits = Number.isFinite(digits) ? Math.max(0, Math.min(10, digits)) : 2;
    return `Preview (${sampleValue}): ${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: safeDigits,
      maximumFractionDigits: safeDigits,
    }).format(sampleValue)}`;
  }

  if (kind === 'currency') {
    const code = (parts[1] || 'USD').toUpperCase();
    const digits = Number(parts[2]);
    const safeDigits = Number.isFinite(digits) ? Math.max(0, Math.min(10, digits)) : 2;
    return `Preview (${sampleValue}): ${new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: safeDigits,
      maximumFractionDigits: safeDigits,
    }).format(sampleValue)}`;
  }

  if (kind === 'percent') {
    const digits = Number(parts[1]);
    const safeDigits = Number.isFinite(digits) ? Math.max(0, Math.min(10, digits)) : 0;
    return `Preview (${sampleValue}): ${new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: safeDigits,
      maximumFractionDigits: safeDigits,
    }).format(sampleValue)}`;
  }

  if (kind === 'compact') {
    return `Preview (${sampleValue}): ${new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(sampleValue)}`;
  }

  return `Preview (${sampleValue}): ${sampleValue}`;
}

/**
 * calculated: type → label → calculation settings → [Advanced: width, prefillLastValue]
 *
 * v1 uses template-driven calculation wiring by referencing field IDs.
 */
export function getCalculatedFieldConfigFields(): FormlyFieldConfig[] {
  return [
    COMMON_FIELD_CONFIGS['type'],
    COMMON_FIELD_CONFIGS['label'],
    {
      key: 'calculationKind',
      type: 'select',
      className: 'flex-basis-full',
      defaultValue: 'date_diff_between_fields',
      props: {
        label: 'formBuilder.calculated.calculationKind',
        required: true,
        options: [
          {
            value: 'date_diff_between_fields',
            label: 'formBuilder.calculated.calculationKind.dateDiffBetweenFields',
          },
          {
            value: 'date_diff_from_previous',
            label: 'formBuilder.calculated.calculationKind.dateDiffFromPrevious',
          },
          {
            value: 'arithmetic_two_fields',
            label: 'formBuilder.calculated.calculationKind.arithmeticTwoFields',
          },
          {
            value: 'checklist_total_metric',
            label: 'formBuilder.calculated.calculationKind.checklistTotalMetric',
          },
          {
            value: 'state_transition',
            label: 'formBuilder.calculated.calculationKind.stateTransition',
          },
        ],
      },
    },
    {
      key: 'inputFieldAId',
      type: 'select',
      className: 'flex-basis-full',
      props: {
        label: 'formBuilder.calculated.inputFieldAId',
        options: [],
      },
      expressionProperties: {
        'props.options':
          'model.calculationKind === "arithmetic_two_fields" ? formState.calculatedNumberFieldOptions : formState.calculatedDateFieldOptions',
      },
      hideExpression: (model: any) =>
        !['date_diff_between_fields', 'arithmetic_two_fields'].includes(model?.calculationKind),
    },
    {
      key: 'inputFieldBId',
      type: 'select',
      className: 'flex-basis-full',
      props: {
        label: 'formBuilder.calculated.inputFieldBId',
        options: [],
      },
      expressionProperties: {
        'props.options':
          'model.calculationKind === "arithmetic_two_fields" ? formState.calculatedNumberFieldOptions : formState.calculatedDateFieldOptions',
      },
      hideExpression: (model: any) =>
        !['date_diff_between_fields', 'arithmetic_two_fields'].includes(model?.calculationKind),
    },
    {
      key: 'currentFieldId',
      type: 'select',
      className: 'flex-basis-full',
      props: {
        label: 'formBuilder.calculated.fieldId',
        options: [],
      },
      expressionProperties: {
        'props.options': 'formState.calculatedDateFieldOptions',
      },
      hideExpression: (model: any) => model?.calculationKind !== 'date_diff_from_previous',
    },
    {
      key: 'checklistFieldId',
      type: 'select',
      className: 'flex-basis-full',
      props: {
        label: 'formBuilder.calculated.checklistFieldId',
        options: [],
      },
      expressionProperties: {
        'props.options': 'formState.calculatedChecklistFieldOptions',
      },
      hideExpression: (model: any) => model?.calculationKind !== 'checklist_total_metric',
    },
    {
      key: 'arithmeticOperator',
      type: 'select',
      className: 'flex-basis-full',
      defaultValue: 'add',
      props: {
        label: 'formBuilder.calculated.arithmeticOperator',
        options: [
          { value: 'add', label: 'formBuilder.calculated.arithmeticOperator.add' },
          { value: 'subtract', label: 'formBuilder.calculated.arithmeticOperator.subtract' },
          { value: 'multiply', label: 'formBuilder.calculated.arithmeticOperator.multiply' },
          { value: 'divide', label: 'formBuilder.calculated.arithmeticOperator.divide' },
        ],
      },
      hideExpression: (model: any) => model?.calculationKind !== 'arithmetic_two_fields',
    },
    {
      key: 'sourceFieldId',
      type: 'select',
      className: 'flex-basis-full',
      props: {
        label: 'formBuilder.calculated.stateSourceFieldId',
        description: 'formBuilder.calculated.stateSourceFieldDescription',
        options: [],
      },
      expressionProperties: {
        'props.options': 'formState.calculatedStateSourceFieldOptions',
      },
      hideExpression: (model: any) => model?.calculationKind !== 'state_transition',
    },
    {
      key: 'outputFieldId',
      type: 'select',
      className: 'flex-basis-full',
      props: {
        label: 'formBuilder.calculated.stateOutputFieldId',
        description: 'formBuilder.calculated.stateOutputFieldDescription',
        options: [],
      },
      expressionProperties: {
        'props.options': 'formState.calculatedStateOutputFieldOptions',
      },
      hideExpression: (model: any) => model?.calculationKind !== 'state_transition',
    },
    {
      key: 'transitions',
      type: 'options-editor',
      className: 'flex-basis-full',
      defaultValue: [{ from: [], to: '' }],
      resetOnHide: false,
      props: {
        label: 'formBuilder.calculated.transitionRules',
        description: 'formBuilder.calculated.transitionRulesDescription',
      },
      fieldArray: {
        fieldGroup: [
          {
            key: 'from',
            type: 'select',
            props: {
              label: 'formBuilder.calculated.transitionFrom',
              description: 'formBuilder.calculated.transitionFromDescription',
              required: true,
              multiple: true,
            },
            expressionProperties: {
              'props.options': (_model: any, formState: any, field?: FormlyFieldConfig) => {
                const sourceFieldId = resolveStateTransitionSourceFieldId(field);
                const optionsByFieldId = formState?.calculatedStateTransitionValueOptionsByFieldId as Record<
                  string,
                  { value: string; label: string }[]
                >;
                return sourceFieldId ? optionsByFieldId?.[sourceFieldId] ?? [] : [];
              },
            },
          },
          {
            key: 'to',
            type: 'select',
            props: {
              label: 'formBuilder.calculated.transitionTo',
              description: 'formBuilder.calculated.transitionToDescription',
              required: true,
            },
            expressionProperties: {
              'props.options': (_model: any, formState: any, field?: FormlyFieldConfig) => {
                const sourceFieldId = resolveStateTransitionSourceFieldId(field);
                const optionsByFieldId = formState?.calculatedStateTransitionValueOptionsByFieldId as Record<
                  string,
                  { value: string; label: string }[]
                >;
                return sourceFieldId ? optionsByFieldId?.[sourceFieldId] ?? [] : [];
              },
            },
          },
        ],
      },
      hideExpression: (model: any) => model?.calculationKind !== 'state_transition',
    },
    {
      key: 'transitionFallback',
      type: 'text',
      className: 'flex-basis-full',
      props: {
        label: 'formBuilder.calculated.transitionFallback',
        placeholder: 'formBuilder.calculated.transitionFallbackPlaceholder',
      },
      hideExpression: (model: any) => model?.calculationKind !== 'state_transition',
    },
    {
      key: 'durationFormat',
      type: 'text',
      className: 'flex-basis-full',
      props: {
        label: 'formBuilder.calculated.durationFormat',
        placeholder: 'formBuilder.calculated.durationFormat.placeholder',
        description: 'formBuilder.calculated.durationFormat.description',
      },
      expressionProperties: {
        'props.description': (model: any) => {
          const base =
            'Use {d}, {h}, {m}, {s} for remainder units, or {total_h}, {total_m}, {total_s} for total units.';
          return `${base} ${previewDuration(model?.durationFormat as string | undefined)}`;
        },
      },
      hideExpression: (model: any) =>
        !['date_diff_between_fields', 'date_diff_from_previous'].includes(model?.calculationKind),
    },
    {
      key: 'numberFormat',
      type: 'text',
      className: 'flex-basis-full',
      props: {
        label: 'formBuilder.calculated.numberFormat',
        placeholder: 'formBuilder.calculated.numberFormat.placeholder',
        description: 'formBuilder.calculated.numberFormat.description',
      },
      expressionProperties: {
        'props.description': (model: any) => {
          const base =
            'Supported: decimal[:digits], integer, currency[:CODE[:digits]], percent[:digits], compact.';
          return `${base} ${previewNumber(model?.numberFormat as string | undefined)}`;
        },
      },
      hideExpression: (model: any) =>
        !['arithmetic_two_fields', 'checklist_total_metric'].includes(model?.calculationKind),
    },
    {
      key: 'emptyBehavior',
      type: 'select',
      className: 'flex-basis-full',
      defaultValue: 'null',
      props: {
        label: 'formBuilder.calculated.emptyBehavior',
        required: true,
        options: [
          { value: 'null', label: 'formBuilder.calculated.emptyBehavior.null' },
          { value: 'zero', label: 'formBuilder.calculated.emptyBehavior.zero' },
        ],
      },
    },
    {
      key: 'persistSnapshot',
      type: 'checkbox',
      className: 'flex-basis-full',
      defaultValue: true,
      props: {
        label: 'formBuilder.calculated.persistSnapshot',
        description: 'formBuilder.calculated.persistSnapshotDescription',
        disabled: true,
      },
    },
    advancedSection(COMMON_FIELD_CONFIGS['width'], COMMON_FIELD_CONFIGS['prefillLastValue']),
  ];
}
